import { useState, useEffect } from 'react';

interface PasswordStrengthMeterProps {
  password: string;
  className?: string;
}

type StrengthLevel = 'empty' | 'weak' | 'medium' | 'strong' | 'very-strong';

export function PasswordStrengthMeter({ 
  password,
  className = ''
}: PasswordStrengthMeterProps) {
  const [strength, setStrength] = useState<StrengthLevel>('empty');
  
  // Password strength calculation
  useEffect(() => {
    if (!password) {
      setStrength('empty');
      return;
    }
    
    let currentScore = 0;
    
    // Length check (up to 5 points)
    currentScore += Math.min(5, Math.floor(password.length / 2));
    
    // Character variety checks
    if (/[A-Z]/.test(password)) currentScore += 2; // Uppercase
    if (/[a-z]/.test(password)) currentScore += 2; // Lowercase
    if (/[0-9]/.test(password)) currentScore += 2; // Numbers
    if (/[^A-Za-z0-9]/.test(password)) currentScore += 3; // Special chars
    
    // Check for variety (at least 3 different character types)
    const varietyCount = [
      /[A-Z]/.test(password),
      /[a-z]/.test(password),
      /[0-9]/.test(password),
      /[^A-Za-z0-9]/.test(password)
    ].filter(Boolean).length;
    
    if (varietyCount >= 3) currentScore += 3;
    
    // Determine strength level
    let newStrength: StrengthLevel = 'empty';
    if (currentScore > 0) newStrength = 'weak';
    if (currentScore >= 8) newStrength = 'medium';
    if (currentScore >= 12) newStrength = 'strong';
    if (currentScore >= 16) newStrength = 'very-strong';
    
    setStrength(newStrength);
  }, [password]);
  
  const getColorClass = () => {
    switch (strength) {
      case 'weak': return 'bg-red-500';
      case 'medium': return 'bg-yellow-500';
      case 'strong': return 'bg-green-500';
      case 'very-strong': return 'bg-primary';
      default: return 'bg-gray-200';
    }
  };
  
  const getWidthPercentage = () => {
    switch (strength) {
      case 'weak': return '25%';
      case 'medium': return '50%';
      case 'strong': return '75%';
      case 'very-strong': return '100%';
      default: return '0%';
    }
  };
  
  const getLabel = () => {
    switch (strength) {
      case 'weak': return 'Weak';
      case 'medium': return 'Medium';
      case 'strong': return 'Strong';
      case 'very-strong': return 'Very Strong';
      default: return '';
    }
  };
  
  return (
    <div className={`space-y-1 ${className}`}>
      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${getColorClass()} transition-all duration-300 ease-in-out`} 
          style={{ width: getWidthPercentage() }}
        ></div>
      </div>
      
      {strength !== 'empty' && (
        <div className="flex justify-between items-center text-xs">
          <span className={`
            ${strength === 'weak' ? 'text-red-600' : ''}
            ${strength === 'medium' ? 'text-yellow-600' : ''}
            ${strength === 'strong' ? 'text-green-600' : ''}
            ${strength === 'very-strong' ? 'text-primary' : ''}
            font-medium
          `}>
            {getLabel()}
          </span>
          
          {strength === 'weak' && (
            <span className="text-gray-500">Add numbers & symbols</span>
          )}
          
          {strength === 'medium' && (
            <span className="text-gray-500">Getting better!</span>
          )}
        </div>
      )}
    </div>
  );
} 